import BigNumber from 'bignumber.js'
import boostPools from 'config/constants/boostPools'
import tokens from 'config/constants/tokens'
import { useTranslation } from 'contexts/Localization'
import { fromUnixTime } from 'date-fns'
import useToast from 'hooks/useToast'
import React, { useCallback, useContext, useState } from 'react'
import { useTransactionAdder } from 'state/transactions/hooks'
import styled from 'styled-components'
import { formatNumber, getBalanceAmount } from 'utils/formatBalance'
import xVvsContext from 'views/xVvs/context'
import useVvsBoostHandlers from 'views/xVvs/hooks/useVvsBoostHandlers'
import { InjectedModalProps, Modal } from 'vvs-uikit'
import ConfirmStep from './ConfirmStep'
import SelectPeriodStep from './SelectPeriodStep'

interface UpgradeModalProps extends InjectedModalProps {
  stakeIds: number[]
  currentPid: number
  amount: BigNumber
  customOnDismiss: () => void
  lastUnlockTime: number
}

const StyledModal = styled(Modal)`
  > div {
    padding-bottom: 4px;
  }
`

const UpgradeModal: React.FC<UpgradeModalProps> = ({
  onDismiss,
  customOnDismiss,
  currentPid,
  stakeIds,
  amount,
  lastUnlockTime,
}) => {
  const { t } = useTranslation()
  const { toastError } = useToast()
  const [pendingTx, setPendingTx] = useState<boolean>()

  const { state, goPrev } = useContext(xVvsContext)
  const { step, selectedPool } = state

  const { onBatchUpgrade } = useVvsBoostHandlers()

  const handleDismiss = useCallback(() => {
    if (customOnDismiss) {
      customOnDismiss()
    }
    onDismiss()
  }, [customOnDismiss, onDismiss])

  const addTransaction = useTransactionAdder()
  const currentLockPeriod = boostPools.find((x) => x.pid === currentPid)?.lockPeriod || 0
  const endDate = fromUnixTime(lastUnlockTime + (selectedPool?.lockPeriod || currentLockPeriod) - currentLockPeriod)
  const handleConfirmClick = async () => {
    setPendingTx(true)
    try {
      const tx = await onBatchUpgrade(
        stakeIds,
        stakeIds.map(() => selectedPool.pid),
      )
      addTransaction(tx, {
        title: t('Vault upgrade completed'),
        summary: t('%amount% %symbol% have been upgraded in vault tier', {
          amount: formatNumber(getBalanceAmount(amount, tokens.xvvs.decimals).toNumber(), 2, 6),
          symbol: tokens.xvvs.symbol,
        }),
      })
      setPendingTx(false)
      handleDismiss()
    } catch (e: any) {
      console.error(e)
      toastError(t('Vault upgrade failed'), t('Your xVVS amount has been reverted'))
      setPendingTx(false)
    }
    handleDismiss()
  }

  return (
    <StyledModal
      title={step === 0 ? t('Upgrade Lock Period') : t('Confirm Upgrade')}
      bodyPadding="0 24px"
      onDismiss={handleDismiss}
      onBack={step > 0 ? goPrev : undefined}
    >
      {step === 0 && <SelectPeriodStep currentPid={currentPid} amount={amount} endDate={endDate} />}
      {step === 1 && (
        <ConfirmStep onConfirm={handleConfirmClick} pendingTx={pendingTx} amount={amount} endDate={endDate} />
      )}
    </StyledModal>
  )
}

export default UpgradeModal
